import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { fetchDrugById } from '../services/drugReferenceService';
import DrugClinicalDataShell, { SourceChip } from './DrugClinicalDataShell';
import { CONFIDENCE_THRESHOLD } from '../config/confidenceThreshold';
import {
  MISSING_DATA_MESSAGE,
  OMC_DISPLAY_LABEL,
  PEDIATRIC_NOT_ESTABLISHED_MESSAGE,
} from '../data/schema';
import { SUPPORTED_COUNTRIES } from '../constants/collections';

function renderText(value) {
  if (value == null || value === '') return MISSING_DATA_MESSAGE;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.join('\n');
  return String(value);
}

function EmergencyEssentialsBlock({ essentials, countryData }) {
  if (!essentials && !countryData?.emergencyEssentials) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main', borderWidth: 2 }}>
        <Typography variant="subtitle2" color="error.main" gutterBottom>
          Emergency Essentials
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {MISSING_DATA_MESSAGE}
        </Typography>
      </Paper>
    );
  }

  const block = essentials || countryData.emergencyEssentials;
  const modes = block.adultDosingByMode || [];

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderColor: 'error.main', borderWidth: 2, overflow: 'hidden' }}>
      <Box sx={{ bgcolor: 'error.main', color: 'error.contrastText', px: 2, py: 1 }}>
        <Typography variant="subtitle2">Emergency Essentials</Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Adult dosing — by mode
        </Typography>
        {modes.length === 0 ? (
          <Typography variant="body2">{MISSING_DATA_MESSAGE}</Typography>
        ) : (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {modes.map((m, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                <Typography variant="body2" component="div">
                  <strong>{m.mode || m.indicationContext}:</strong>{' '}
                  {[m.dose, m.route, m.frequency, m.concentration, m.notes].filter(Boolean).join(' · ')}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Concentration / how supplied
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {(block.concentrationSupplied || []).length
            ? block.concentrationSupplied.map((line, i) => <div key={i}>{line}</div>)
            : MISSING_DATA_MESSAGE}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Warnings that kill
        </Typography>
        {(block.warningsThatKill || []).length ? (
          <Box component="ul" sx={{ pl: 2, m: 0, mb: 2 }}>
            {block.warningsThatKill.map((w, i) => (
              <Typography component="li" variant="body2" key={i}>
                {w}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {MISSING_DATA_MESSAGE}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Reversal / rescue
        </Typography>
        <Typography variant="body2">{renderText(block.reversalRescue)}</Typography>
      </Box>
    </Paper>
  );
}

function PediatricSection({ pediatric }) {
  const status = pediatric?.status;
  const hasDosing = Array.isArray(pediatric?.dosing) && pediatric.dosing.length > 0;

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label="PEDS" color="primary" />
          <Typography variant="subtitle2">Pediatric Dosing &amp; Warnings</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {pediatric?.dataSource && (
          <SourceChip source={pediatric.dataSource} retrievedAt={pediatric.sourceRetrievedAt} />
        )}
        {!hasDosing || status === 'not-established' || status === 'not-available' ? (
          <Typography variant="body2" color="text.secondary">
            {PEDIATRIC_NOT_ESTABLISHED_MESSAGE}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {pediatric.dosing.map((d, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2">
                  <strong>{d.mode || d.ageBand}:</strong> {d.dose}
                  {d.maxDose ? ` · max ${d.maxDose}` : ''}
                  {d.route ? ` · ${d.route}` : ''}
                  {d.frequency ? ` · ${d.frequency}` : ''}
                </Typography>
              </Paper>
            ))}
            {(pediatric.warnings || []).map((w, i) => (
              <Alert severity="warning" key={i} sx={{ py: 0.5 }}>
                {w}
              </Alert>
            ))}
          </Stack>
        )}
        {pediatric?.requiresReview && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Requires extra confirmation — pediatric data pending review
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function DetailAccordion({ title, content, source, retrievedAt }) {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {source && <SourceChip source={source} retrievedAt={retrievedAt} />}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {renderText(content)}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

function OtherMedsSection({ omc }) {
  if (!omc?.items?.length) return null;
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">{OMC_DISPLAY_LABEL}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These are not interchangeable substitutes — drugs a clinician may also consider in overlapping
          situations.
        </Typography>
        <Stack spacing={1}>
          {omc.items.map((item) => (
            <Paper key={item.drugId} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">{item.name}</Typography>
                <Chip size="small" label={item.interchangeability} />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block">
                {item.relationship?.replace(/-/g, ' ')}
              </Typography>
              <Typography variant="body2">{item.note}</Typography>
              {item.requiresReview && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                  Requires extra confirmation
                </Alert>
              )}
            </Paper>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function DrugDetail() {
  const { drugId } = useParams();
  const [loading, setLoading] = useState(true);
  const [drug, setDrug] = useState(null);
  const [country, setCountry] = useState('US');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchDrugById(drugId);
        if (!cancelled) setDrug(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load drug.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drugId]);

  const countryData = useMemo(() => drug?.countries?.[country] || null, [drug, country]);
  const omc = drug?.otherMedicationsForConsideration?.['emergency-medicine'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!drug) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Drug not found.</Alert>
        <Button component={RouterLink} to="/dashboard/drug-reference" sx={{ mt: 2 }}>
          Back to Drug Reference
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 740, mx: 'auto' }}>
      <Button
        component={RouterLink}
        to="/dashboard/drug-reference"
        startIcon={<ArrowBackRounded />}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderLeft: 4, borderColor: 'error.main', mb: 2 }}>
        <Box sx={{ bgcolor: 'error.main', color: 'error.contrastText', p: 2 }}>
          {drug.highAlert && (
            <Chip
              size="small"
              label="High-alert medication"
              sx={{ bgcolor: 'background.paper', color: 'error.main', mb: 1 }}
            />
          )}
          <Typography variant="h4">{drug.genericName || drug.id}</Typography>
          {drug.alternateNames?.length > 0 && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Also known as: {drug.alternateNames.join(', ')}
            </Typography>
          )}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {(drug.classification || []).map((c) => (
              <Chip key={c} size="small" label={c} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }} />
            ))}
          </Stack>
        </Box>
      </Paper>

      <Tabs value={country} onChange={(_, v) => setCountry(v)} sx={{ mb: 0 }}>
        {SUPPORTED_COUNTRIES.map((code) => (
          <Tab key={code} value={code} label={code} />
        ))}
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2, borderTopLeftRadius: 0 }}>
        <DrugClinicalDataShell
          countryCode={country}
          confidenceScore={countryData?.confidenceScore}
          requiresReview={countryData?.requiresReview}
        >
          {!countryData && country !== 'US' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No {country} data available. Dosing is not cross-filled from other countries.
            </Alert>
          )}

          {countryData?.dataSource?.includes('curated') && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Requires extra confirmation — {country} data is from a curated mapping, not a live regulator
              feed.
            </Alert>
          )}

          {country === 'US' && (
            <>
              <EmergencyEssentialsBlock countryData={countryData} />
              <PediatricSection pediatric={countryData?.pediatric} />
              <DetailAccordion
                title="Indication"
                content={countryData?.indications}
                source={countryData?.fieldSources?.indications?.source || countryData?.dataSource}
                retrievedAt={countryData?.fieldSources?.indications?.retrievedAt || countryData?.sourceRetrievedAt}
              />
              <DetailAccordion
                title="Contraindications"
                content={countryData?.contraindications}
                source={countryData?.fieldSources?.contraindications?.source || countryData?.dataSource}
                retrievedAt={countryData?.fieldSources?.contraindications?.retrievedAt || countryData?.sourceRetrievedAt}
              />
              <DetailAccordion
                title="Warnings & Precautions (full)"
                content={countryData?.warnings}
                source={countryData?.fieldSources?.warnings?.source || countryData?.dataSource}
                retrievedAt={countryData?.fieldSources?.warnings?.retrievedAt || countryData?.sourceRetrievedAt}
              />
              <DetailAccordion
                title="Compatibility & Line Essentials"
                content={countryData?.compatibility}
                source="curated · pharmacy protocol"
              />
              <DetailAccordion
                title="Monitoring & Endpoints"
                content={countryData?.monitoring}
                source="curated"
              />
              <DetailAccordion
                title="Critical Interactions (emergent only)"
                content={countryData?.criticalInteractions}
                source={countryData?.fieldSources?.drugInteractions?.source || countryData?.dataSource}
              />
              <DetailAccordion
                title="Administration Pearls / Time-Critical"
                content={countryData?.administrationPearls}
                source="curated · clinical pearls"
              />
              <DetailAccordion
                title="Pregnancy / Lactation"
                content={countryData?.pregnancyLactation}
                source={countryData?.fieldSources?.pregnancyLactation?.source || countryData?.dataSource}
              />
              <DetailAccordion
                title="Mechanism (brief)"
                content={countryData?.mechanismBrief || drug.baseData?.mechanism}
                source={drug.baseData?._source || countryData?.dataSource}
              />
              <OtherMedsSection omc={omc} />
            </>
          )}

          {country !== 'US' && countryData && (
            <DetailAccordion
              title={`${country} naming & status`}
              content={[
                countryData.localGenericName && `Local generic: ${countryData.localGenericName}`,
                countryData.brandNames?.length && `Brand names: ${countryData.brandNames.join(', ')}`,
                countryData.legalClassification && `Legal class: ${countryData.legalClassification}`,
              ]
                .filter(Boolean)
                .join('\n')}
              source={countryData.dataSource}
              retrievedAt={countryData.sourceRetrievedAt}
            />
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Version {drug.version ?? 0} · Updated{' '}
            {drug.updatedAt ? new Date(drug.updatedAt).toLocaleDateString() : '—'}
            {countryData?.confidenceScore != null &&
              ` · Confidence ${countryData.confidenceScore.toFixed(2)} (threshold ${CONFIDENCE_THRESHOLD})`}
          </Typography>
        </DrugClinicalDataShell>
      </Paper>
    </Box>
  );
}
